import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useEntityArtifacts } from '../Capture/documentHooks';
import { useRfiQuestions } from './rfiQuestionHooks';
import { useRfiSections } from './rfiSectionHooks';
import { useRfiSourceVersionMutations, useRfiSourceVersions } from './rfiSourceVersionHooks';
const KIND = {
    added: 'Added',
    removed: 'Removed',
    modified: 'Modified'
};
const CATEGORY = {
    date: 'Date',
    question: 'Question',
    requirement: 'Requirement',
    other: 'Other'
};
const STATE = {
    pending: 'Awaiting review',
    accepted: 'Accepted',
    rejected: 'Rejected'
};
/**
 * Versioned RFI source documents. An amendment is recorded as a new version
 * linked to the previous one; its change review is shown before anyone accepts it.
 */ export function RfiSourceVersionsPanel({ rfiResponseProjectId }) {
    const versions = useRfiSourceVersions(rfiResponseProjectId);
    const artifacts = useEntityArtifacts('rfi_response_project', rfiResponseProjectId);
    const questions = useRfiQuestions(rfiResponseProjectId);
    const sections = useRfiSections(rfiResponseProjectId);
    const { record, review } = useRfiSourceVersionMutations(rfiResponseProjectId);
    const [artifactId, setArtifactId] = useState('');
    const list = versions.data ?? [];
    const candidates = (artifacts.data ?? []).filter((a)=>!list.some((v)=>v.artifactId === a.artifactId && v.artifactVersion === a.version && v.sha256 === a.checksumSha256));
    const filename = (id)=>artifacts.data?.find((a)=>a.artifactId === id)?.filename ?? id;
    const quote = (id)=>questions.data?.questions.find((q)=>q.id === id)?.source?.originalText ?? 'Question unavailable';
    const title = (id)=>sections.data?.find((s)=>s.id === id)?.title ?? 'Section unavailable';
    const error = versions.error ?? record.error ?? review.error;
    return /*#__PURE__*/ _jsxs("section", {
        className: "max-w-3xl space-y-3",
        "data-testid": Testids.rfiSourceVersions.panel,
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: "text-lg font-medium",
                children: "Source versions and amendments"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Documents uploads automatically record source versions. You can also record an existing attachment here. Each new version is linked to the previous one; earlier versions and their files are never changed."
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-end gap-2",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Document to record as the next source version",
                        "data-testid": Testids.rfiSourceVersions.artifact,
                        value: artifactId,
                        onChange: (e)=>setArtifactId(e.target.value),
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Choose an uploaded document"
                            }),
                            candidates.map((a)=>/*#__PURE__*/ _jsx("option", {
                                    value: a.artifactId,
                                    children: a.filename
                                }, a.artifactId))
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.rfiSourceVersions.record,
                        disabled: !artifactId || record.isPending,
                        onClick: ()=>record.mutate(artifactId, {
                                onSuccess: ()=>setArtifactId('')
                            }),
                        children: list.length ? 'Record amendment' : 'Record original source'
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: error instanceof Error ? error.message : 'Source versions failed'
            }),
            list.map((v)=>/*#__PURE__*/ _jsxs("article", {
                    className: "space-y-2 rounded border p-3",
                    "data-testid": Testids.rfiSourceVersions.row,
                    "data-version-no": v.versionNo,
                    "data-artifact-id": v.artifactId,
                    "data-review-state": v.reviewState,
                    children: [
                        /*#__PURE__*/ _jsxs("header", {
                            className: "flex items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsxs("strong", {
                                    children: [
                                        "Version ",
                                        v.versionNo,
                                        ": ",
                                        filename(v.artifactId)
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                    children: v.kind === 'original' ? 'Original' : 'Amendment'
                                }),
                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                    children: STATE[v.reviewState] ?? v.reviewState
                                })
                            ]
                        }),
                        v.priorVersionId && /*#__PURE__*/ _jsxs("p", {
                            className: "text-sm",
                            children: [
                                "Amends version ",
                                list.find((p)=>p.id === v.priorVersionId)?.versionNo
                            ]
                        }),
                        v.kind === 'amendment' && /*#__PURE__*/ _jsxs("div", {
                            "data-testid": Testids.rfiSourceVersions.changes,
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-sm font-medium",
                                    children: "What changed"
                                }),
                                v.diffState === 'unavailable' ? /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        "Change review unavailable: ",
                                        v.diffError
                                    ]
                                }) : v.changes.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                    children: "No meaningful changes found."
                                }) : /*#__PURE__*/ _jsx("ul", {
                                    children: v.changes.map((c, i)=>/*#__PURE__*/ _jsxs("li", {
                                            children: [
                                                /*#__PURE__*/ _jsxs("strong", {
                                                    children: [
                                                        KIND[c.kind] ?? c.kind,
                                                        " ",
                                                        CATEGORY[c.category]?.toLowerCase()
                                                    ]
                                                }),
                                                c.before && /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        " from “",
                                                        c.before,
                                                        "”"
                                                    ]
                                                }),
                                                c.after && /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        " to “",
                                                        c.after,
                                                        "”"
                                                    ]
                                                })
                                            ]
                                        }, i))
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    "data-testid": Testids.rfiSourceVersions.impact,
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-sm font-medium",
                                            children: "Impact on the response"
                                        }),
                                        v.affectedQuestionIds.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                            children: "No recorded questions are affected."
                                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsxs("p", {
                                                    children: [
                                                        "Affected questions: ",
                                                        v.affectedQuestionIds.map(quote).join('; ')
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("p", {
                                                    children: [
                                                        "Affected sections:",
                                                        ' ',
                                                        v.affectedSectionIds.length ? v.affectedSectionIds.map(title).join(', ') : 'none mapped yet'
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        v.reviewState === 'pending' ? /*#__PURE__*/ _jsxs("div", {
                            className: "flex gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": Testids.rfiSourceVersions.accept,
                                    disabled: review.isPending,
                                    onClick: ()=>review.mutate({
                                            versionId: v.id,
                                            decision: 'accepted'
                                        }),
                                    children: "Accept into the working response"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": Testids.rfiSourceVersions.reject,
                                    disabled: review.isPending,
                                    onClick: ()=>review.mutate({
                                            versionId: v.id,
                                            decision: 'rejected'
                                        }),
                                    children: "Reject"
                                })
                            ]
                        }) : /*#__PURE__*/ _jsxs("p", {
                            className: "text-sm",
                            children: [
                                STATE[v.reviewState],
                                " by ",
                                v.reviewedBy,
                                " at",
                                ' ',
                                v.reviewedAt ? new Date(Number(v.reviewedAt.seconds) * 1000).toLocaleString() : ''
                            ]
                        })
                    ]
                }, v.id))
        ]
    });
}
